// @ts-nocheck
/* Base File Viewer dialog. Fetches campaign numbers via
 * API_VOICE_CAMPAIGNS_NUMBERS, supports "go-to line", warning + truncation
 * banners, source-label footer. */
import { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getApiCall } from '@/service/httpCall';
import { API_VOICE_CAMPAIGNS_NUMBERS } from '@/service/url';

const EMPTY_STATE: any = {
  numbers: [],
  total: 0,
  expectedTotal: 0,
  truncated: false,
  source: null,
  baseName: null,
  warning: null,
  error: null,
};

const NumbersViewerModal = ({ show, onHide, campaignId, campaignName, expectedTotal }) => {
  const [state, setState] = useState(EMPTY_STATE);
  const [loading, setLoading] = useState(false);
  const [lineInput, setLineInput] = useState('');
  const listRef = useRef(null);
  const rowRefs = useRef({} as any);

  const { numbers, warning, error, truncated, source, baseName } = state;

  useEffect(() => {
    if (show && campaignId) {
      setLoading(true);
      setState(EMPTY_STATE);
      setLineInput('');
      const url = API_VOICE_CAMPAIGNS_NUMBERS.replace(':id', campaignId);
      getApiCall(url, {})
        .then((res: any) => {
          setState({
            numbers: Array.isArray(res?.numbers) ? res.numbers : [],
            total: Number(res?.total) || 0,
            expectedTotal: Number(res?.expected_total) || 0,
            truncated: !!res?.truncated,
            source: res?.source || null,
            baseName: res?.base_name || null,
            warning: res?.warning || null,
            error: null,
          });
        })
        .catch((err: any) =>
          setState({
            ...EMPTY_STATE,
            error:
              typeof err === 'string'
                ? err
                : err?.message || 'Unable to load numbers for this campaign.',
          }),
        )
        .finally(() => setLoading(false));
    }
    if (!show) {
      setState(EMPTY_STATE);
      setLineInput('');
    }
  }, [show, campaignId]);

  const goToLine = () => {
    const n = parseInt(lineInput, 10);
    if (!n || n < 1 || n > numbers.length) return;
    const el = rowRefs.current[n - 1];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const claimedTotal = Number(state.expectedTotal) || Number(expectedTotal) || 0;
  const showMismatch = !loading && !error && claimedTotal > 0 && numbers.length === 0;

  const sourceLabel =
    source === 'contact_groups'
      ? 'Contact group'
      : source === 'base_temp_file'
        ? 'Uploaded file (unprocessed)'
        : baseName || (source === 'base_file' ? 'Base file' : null);

  return (
    <Dialog open={!!show} onOpenChange={(next) => !next && onHide?.()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Base File Viewer
            {(campaignId || campaignName) && (
              <div className="mt-0.5 text-xs font-normal text-muted-foreground">
                {campaignName ? `${campaignName} ` : ''}
                {campaignId ? `(ID: ${campaignId})` : ''} — view only
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        {!loading && error && (
          <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {!loading && !error && (warning || showMismatch || truncated) && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
            <div className="space-y-1">
              {warning && <div>{warning}</div>}
              {showMismatch && (
                <div>
                  This campaign reports {claimedTotal} number{claimedTotal === 1 ? '' : 's'}, but
                  none could be read.
                </div>
              )}
              {truncated && (
                <div>
                  Showing the first {numbers.length.toLocaleString()} of{' '}
                  {state.total.toLocaleString()} numbers.
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && !error && sourceLabel && numbers.length > 0 && (
          <div className="mb-2 text-xs text-muted-foreground">Source: {sourceLabel}</div>
        )}

        <div className="mb-3 flex gap-2">
          <Input
            type="number"
            placeholder="Line number"
            value={lineInput}
            min={1}
            onChange={(e) => setLineInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && goToLine()}
          />
          <Button onClick={goToLine} disabled={!numbers.length}>
            Go
          </Button>
        </div>

        <div
          ref={listRef}
          className="max-h-[340px] overflow-y-auto rounded-md border border-border"
        >
          {loading ? (
            <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : numbers.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">No numbers found</div>
          ) : (
            numbers.map((num, idx) => (
              <div
                key={idx}
                ref={(el) => (rowRefs.current[idx] = el)}
                className="flex items-center border-b border-border px-3 py-2 text-sm"
              >
                <span className="w-12 flex-none">{idx + 1}.</span>
                <span>{num}</span>
              </div>
            ))
          )}
        </div>

        <DialogFooter className="!justify-between">
          <span className="text-sm text-muted-foreground">
            Total lines: {numbers.length}
            {claimedTotal > 0 && claimedTotal !== numbers.length
              ? ` (campaign total: ${claimedTotal})`
              : ''}
          </span>
          <Button variant="outline" onClick={onHide}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NumbersViewerModal;
